import { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Alert, Button, Card, KpiCard, Spinner, WidgetShell,
  BaseAreaChart, BaseBarChart, BaseDonutChart,
} from '@kss/ui';
import { apiClient } from '@kss/api';
import {
  BarChart3, CalendarCheck, PieChart, RefreshCw, TrendingUp, Users,
} from 'lucide-react';

/**
 * Live tenant analytics — consumes the §15 backend endpoints shipped
 * 2026-05-14:
 *
 *   GET /api/analytics/headcount-trend?months=12
 *   GET /api/analytics/attendance-week[?weekStart=YYYY-MM-DD]
 *   GET /api/analytics/department-mix
 *
 * Each endpoint returns a pre-computed aggregate so the page doesn't
 * pull the full Employee / Attendance lists. Tabs let the operator
 * drill into one widget at a time without re-fetching unrelated ones.
 *
 * The page never falls back to fake data. If an endpoint returns an
 * empty array, we render an empty-state card with a hint pointing at
 * the seed action so it's obvious the API is reachable but has nothing
 * to plot yet.
 */

type Tab = 'overview' | 'headcount' | 'attendance' | 'departments';

interface HeadcountPoint { month: string; count: number }
interface AttendanceDay  { day: string; date?: string; present: number; absent: number; onLeave?: number }
interface DepartmentSlice { department: string; count: number; activeCount?: number }

export default function AnalyticsLive() {
  const [params, setParams] = useSearchParams();
  const tab = (params.get('tab') as Tab) || 'overview';
  function setTab(next: Tab) {
    const p = new URLSearchParams(params);
    p.set('tab', next);
    setParams(p, { replace: true });
  }

  const [months, setMonths] = useState(12);
  const [headcount, setHeadcount] = useState<HeadcountPoint[]>([]);
  const [attendance, setAttendance] = useState<AttendanceDay[]>([]);
  const [departments, setDepartments] = useState<DepartmentSlice[]>([]);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [lastFetched, setLastFetched] = useState<Date | null>(null);

  async function load() {
    setLoading(true);
    setErr(null);
    try {
      const [hc, att, dept] = await Promise.all([
        apiClient.get<HeadcountPoint[]>(`/analytics/headcount-trend?months=${months}`).catch(() => ({ data: [] })),
        apiClient.get<AttendanceDay[]>(`/analytics/attendance-week`).catch(() => ({ data: [] })),
        apiClient.get<DepartmentSlice[]>(`/analytics/department-mix`).catch(() => ({ data: [] })),
      ]);
      setHeadcount(pickArray(hc.data) as HeadcountPoint[]);
      setAttendance(pickArray(att.data) as AttendanceDay[]);
      setDepartments(pickArray(dept.data) as DepartmentSlice[]);
      setLastFetched(new Date());
    } catch (e) {
      setErr(e instanceof Error ? e.message : 'Failed to load analytics');
    } finally {
      setLoading(false);
    }
  }
  useEffect(() => { void load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [months]);

  const kpis = useMemo(() => {
    const current = headcount[headcount.length - 1]?.count ?? 0;
    const six = headcount[headcount.length - 7]?.count ?? current;
    const growth = current - six;
    const present = attendance.reduce((s, d) => s + (d.present ?? 0), 0);
    const expected = attendance.reduce((s, d) => s + (d.present ?? 0) + (d.absent ?? 0), 0);
    const presentRate = expected > 0 ? Math.round((present / expected) * 100) : 0;
    const topDept = [...departments].sort((a, b) => b.count - a.count)[0];
    return {
      current,
      growth,
      presentRate,
      departments: departments.length,
      topDept: topDept?.department ?? '—',
      topDeptCount: topDept?.count ?? 0,
    };
  }, [headcount, attendance, departments]);

  return (
    <div className="space-y-4">
      <header className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-border bg-surface-card px-4 py-3">
        <div className="min-w-0 flex-1">
          <h1 className="text-base font-semibold text-content-primary">Live analytics</h1>
          <p className="text-xs text-content-muted">
            Pre-computed aggregates from <code>/api/analytics/*</code> (§15) ·
            tenant-scoped · {lastFetched ? `refreshed ${relativeTime(lastFetched)}` : 'loading'}
          </p>
        </div>
        <select
          value={months}
          onChange={(e) => setMonths(Number(e.target.value))}
          className="h-9 rounded-md border border-border bg-surface-card px-2 text-sm"
          title="Headcount trend lookback"
        >
          <option value={6}>Last 6 months</option>
          <option value={12}>Last 12 months</option>
          <option value={24}>Last 24 months</option>
        </select>
        <Button variant="outline" size="sm" onClick={load} disabled={loading}>
          <RefreshCw className={`h-3.5 w-3.5 ${loading ? 'animate-spin' : ''}`} /> Reload
        </Button>
      </header>

      {err && <Alert variant="danger">{err}</Alert>}

      {/* KPI strip — always visible */}
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <KpiCard
          title="Active headcount"
          value={kpis.current.toLocaleString()}
          subtitle={
            kpis.growth === 0
              ? 'No change vs 6m ago'
              : kpis.growth > 0
                ? `+${kpis.growth.toLocaleString()} since 6m ago`
                : `${kpis.growth.toLocaleString()} since 6m ago`
          }
          icon={<Users className="h-5 w-5 text-info" />}
        />
        <KpiCard
          title="Present rate (week)"
          value={`${kpis.presentRate}%`}
          subtitle="Present ÷ expected"
          icon={<CalendarCheck className={`h-5 w-5 ${kpis.presentRate >= 85 ? 'text-success' : 'text-warning'}`} />}
        />
        <KpiCard
          title="Departments"
          value={kpis.departments.toLocaleString()}
          subtitle={kpis.topDept !== '—' ? `Largest: ${kpis.topDept} (${kpis.topDeptCount})` : 'None yet'}
          icon={<PieChart className="h-5 w-5 text-primary" />}
        />
        <KpiCard
          title="Lookback"
          value={`${months} months`}
          subtitle="Headcount trend window"
          icon={<TrendingUp className="h-5 w-5 text-success" />}
        />
      </div>

      <Tabs current={tab} onChange={setTab} />

      {tab === 'overview' && (
        <div className="grid gap-4 lg:grid-cols-2">
          <HeadcountWidget rows={headcount} loading={loading} />
          <AttendanceWidget rows={attendance} loading={loading} />
          <div className="lg:col-span-2">
            <DepartmentWidget rows={departments} loading={loading} />
          </div>
        </div>
      )}
      {tab === 'headcount'   && <HeadcountWidget rows={headcount} loading={loading} expanded />}
      {tab === 'attendance'  && <AttendanceWidget rows={attendance} loading={loading} expanded />}
      {tab === 'departments' && <DepartmentWidget rows={departments} loading={loading} expanded />}

      {loading && (
        <div className="flex items-center gap-2 text-xs text-content-muted">
          <Spinner /> Querying analytics endpoints…
        </div>
      )}
    </div>
  );
}

/* -------------------------------------------------------------------- */
/*  Tabs                                                                */
/* -------------------------------------------------------------------- */

function Tabs({ current, onChange }: { current: Tab; onChange: (t: Tab) => void }) {
  const items: { id: Tab; label: string; icon: React.ReactNode }[] = [
    { id: 'overview',    label: 'Overview',        icon: <BarChart3 size={13} /> },
    { id: 'headcount',   label: 'Headcount trend', icon: <TrendingUp size={13} /> },
    { id: 'attendance',  label: 'Attendance week', icon: <CalendarCheck size={13} /> },
    { id: 'departments', label: 'Department mix',  icon: <PieChart size={13} /> },
  ];
  return (
    <div className="flex flex-wrap gap-1 border-b border-border">
      {items.map((it) => (
        <button
          key={it.id}
          type="button"
          onClick={() => onChange(it.id)}
          className={`inline-flex items-center gap-1.5 border-b-2 px-3 py-2 text-sm transition-colors ${
            current === it.id
              ? 'border-primary font-medium text-primary'
              : 'border-transparent text-content-secondary hover:text-content-primary'
          }`}
        >
          {it.icon} {it.label}
        </button>
      ))}
    </div>
  );
}

/* -------------------------------------------------------------------- */
/*  Widgets                                                             */
/* -------------------------------------------------------------------- */

function HeadcountWidget({
  rows, loading, expanded,
}: { rows: HeadcountPoint[]; loading: boolean; expanded?: boolean }) {
  if (!loading && rows.length === 0) {
    return (
      <WidgetShell title="Headcount trend">
        <EmptyChart hint="No employee history yet for this tenant. Seed via /panel/employees/new or /panel/api/admin/migration/grant-admin-everything." />
      </WidgetShell>
    );
  }
  return (
    <WidgetShell title="Headcount trend">
      <BaseAreaChart
        data={rows.map((r) => ({ month: r.month, value: r.count }))}
        areas={[{ dataKey: 'value', name: 'Active' }]}
        xAxisKey="month"
        height={expanded ? 380 : 260}
      />
    </WidgetShell>
  );
}

function AttendanceWidget({
  rows, loading, expanded,
}: { rows: AttendanceDay[]; loading: boolean; expanded?: boolean }) {
  if (!loading && rows.length === 0) {
    return (
      <WidgetShell title="Attendance this week">
        <EmptyChart hint="The Attendance entity needs EmployeeId + Date columns to populate this chart. See BACKEND_AGENT_PROMPTS.md §15 caveat." />
      </WidgetShell>
    );
  }
  return (
    <WidgetShell title="Attendance this week">
      <BaseBarChart
        data={rows.map((r) => ({ name: r.day, present: r.present ?? 0, absent: r.absent ?? 0 }))}
        bars={[
          { dataKey: 'present', name: 'Present', stackId: 'week' },
          { dataKey: 'absent',  name: 'Absent',  stackId: 'week' },
        ]}
        xAxisKey="name"
        height={expanded ? 380 : 260}
        showLegend
      />
    </WidgetShell>
  );
}

function DepartmentWidget({
  rows, loading, expanded,
}: { rows: DepartmentSlice[]; loading: boolean; expanded?: boolean }) {
  if (!loading && rows.length === 0) {
    return (
      <WidgetShell title="Department mix">
        <EmptyChart hint="No employees with a department set. Seed Departments and re-run." />
      </WidgetShell>
    );
  }
  return (
    <WidgetShell title="Department mix">
      <BaseDonutChart
        data={rows.map((r) => ({ name: r.department, value: r.count }))}
        height={expanded ? 380 : 280}
      />
    </WidgetShell>
  );
}

function EmptyChart({ hint }: { hint: string }) {
  return (
    <div className="flex h-[200px] flex-col items-center justify-center gap-1 px-4 text-center text-xs text-content-muted">
      <BarChart3 className="h-6 w-6 opacity-40" />
      <div>Live data — no rows yet.</div>
      <div className="text-[10px]">{hint}</div>
    </div>
  );
}

/* -------------------------------------------------------------------- */
/*  Helpers                                                             */
/* -------------------------------------------------------------------- */

function pickArray(d: unknown): unknown[] {
  if (Array.isArray(d)) return d;
  if (d && typeof d === 'object') {
    const obj = d as { items?: unknown[]; data?: unknown[] };
    if (Array.isArray(obj.items)) return obj.items;
    if (Array.isArray(obj.data)) return obj.data;
  }
  return [];
}

function relativeTime(d: Date): string {
  const diff = Math.round((Date.now() - d.getTime()) / 1000);
  if (diff < 60) return `${diff}s ago`;
  if (diff < 3600) return `${Math.round(diff / 60)}m ago`;
  return `${Math.round(diff / 3600)}h ago`;
}
